import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import SideRenderer from "../components/SideRenderer";
import BuildFooter from "../components/BuildFooter";

export default function Decks() {
  const qc = useQueryClient();
  const decks = useQuery({ queryKey: ["decks"], queryFn: () => api.listDecks() });
  const [newName, setNewName] = useState("");
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const search = useQuery({
    queryKey: ["search", trimmedQuery],
    queryFn: () => api.searchNotes(trimmedQuery),
    enabled: trimmedQuery.length > 0,
  });

  const createDeck = useMutation({
    mutationFn: (name: string) => api.createDeck(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decks"] }),
  });

  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.clear();
      window.location.href = "/login";
    },
  });

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    await createDeck.mutateAsync(name);
    setNewName("");
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <header className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">Decks</h1>
        <div className="flex items-center gap-2">
          <Link to="/profile" className="btn-secondary text-sm">
            Profile
          </Link>
          <Link to="/logs" className="btn-secondary text-sm">
            Logs
          </Link>
          <button onClick={() => logout.mutate()} className="btn-secondary text-sm">
            Sign out
          </button>
        </div>
      </header>

      <form onSubmit={onCreate} className="flex gap-2">
        <input
          type="text"
          placeholder="New deck name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="input"
        />
        <button type="submit" className="btn-primary" disabled={createDeck.isPending}>
          Add
        </button>
      </form>

      <div className="relative">
        <input
          type="search"
          placeholder="Search all cards…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input pr-10"
          aria-label="Search all cards"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 px-2"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {trimmedQuery.length > 0 ? (
        <section className="space-y-3">
          {search.isLoading && (
            <div className="text-slate-500 dark:text-slate-400">Searching…</div>
          )}
          {search.data && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {search.data.length} match{search.data.length === 1 ? "" : "es"}
            </div>
          )}
          {search.data && search.data.length === 0 && !search.isLoading && (
            <div className="text-slate-500 dark:text-slate-400">No cards match.</div>
          )}
          <ul className="space-y-3">
            {search.data?.map((note) => (
              <li key={note.id} className="card-surface p-4">
                <Link
                  to={`/decks/${note.deckId}/notes/${note.id}`}
                  state={{ from: "/" }}
                  className="block space-y-2"
                >
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    in <span className="font-medium">{note.deckName}</span>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Front</div>
                      <SideRenderer side={note.front} small />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Back</div>
                      <SideRenderer side={note.back} small />
                    </div>
                  </div>
                  {(note.description.text || note.description.image) && (
                    <div className="pt-1 border-t border-slate-100 dark:border-slate-700">
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Description</div>
                      {note.description.text && (
                        <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words">
                          {note.description.text}
                        </div>
                      )}
                      {note.description.image && (
                        <img
                          src={api.imageUrl(note.description.image)}
                          alt=""
                          className="mt-1 rounded-lg max-h-24"
                        />
                      )}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <>

      {decks.isLoading && <div className="text-slate-500 dark:text-slate-400">Loading…</div>}

      {decks.data && decks.data.length === 0 && (
        <div className="text-slate-500 dark:text-slate-400">No decks yet — create one above.</div>
      )}

      <ul className="space-y-3">
        {decks.data?.map((deck) => {
          const studyable = deck.dueCount + deck.newCount;
          return (
            <li key={deck.id} className="card-surface p-4 flex items-center gap-2 sm:gap-3">
              <Link to={`/decks/${deck.id}`} className="flex-1 min-w-0">
                <div className="font-medium truncate">{deck.name}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {deck.noteCount} note{deck.noteCount === 1 ? "" : "s"}
                  {" · "}
                  {deck.dueCount} due, {deck.newCount} new
                </div>
              </Link>
              <Link
                to={`/decks/${deck.id}/new`}
                className="btn-secondary text-sm whitespace-nowrap"
                aria-label={`Add a card to ${deck.name}`}
              >
                + Add
              </Link>
              <Link
                to={`/decks/${deck.id}/options`}
                className="btn-secondary text-sm whitespace-nowrap"
                aria-label={`Options for ${deck.name}`}
                title="Options"
              >
                ⚙
              </Link>
              <Link
                to={`/decks/${deck.id}/study`}
                className={`btn-primary text-sm whitespace-nowrap ${studyable === 0 ? "opacity-50 pointer-events-none" : ""}`}
              >
                Study
              </Link>
            </li>
          );
        })}
      </ul>
        </>
      )}

      <BuildFooter />
    </div>
  );
}
